import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordenesVehiculosAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Car,
    Building2,
    Cpu,
    Calendar,
    Hash,
    FileText,
    Edit,
} from 'lucide-react';

export default function VehiculoDetallePage() {
    const { vehiculoId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [vehiculo, setVehiculo] = useState(null);

    useEffect(() => {
        loadVehiculo();
    }, [vehiculoId]);

    const loadVehiculo = async () => {
        try {
            setLoading(true);
            const res = await ordenesVehiculosAPI.getById(vehiculoId);
            setVehiculo(res.data);
        } catch (err) {
            console.error('Error loading vehiculo:', err);
            toast.error('Error al cargar vehículo');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!vehiculo) {
        return (
            <div className="p-6">
                <div className="text-center py-12">
                    <Car className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-muted-foreground">Vehículo no encontrado</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/ordenes/vehiculos')}>
                        Volver a Vehículos
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/vehiculos')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Car className="h-6 w-6" />
                            {vehiculo.patente}
                        </h1>
                        <p className="text-muted-foreground">Detalle del vehículo</p>
                    </div>
                </div>
                <Button variant="outline" onClick={() => navigate('/ordenes/vehiculos')} data-testid="edit-btn">
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                </Button>
            </div>

            {/* Info Principal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Datos del Vehículo */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Car className="h-5 w-5" />
                            Datos del Vehículo
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Patente</p>
                                <p className="font-mono font-bold text-lg">{vehiculo.patente}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Tipo</p>
                                <p className="font-medium">{vehiculo.tipo_vehiculo || vehiculo.tipo || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Marca</p>
                                <p className="font-medium">{vehiculo.marca || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Modelo</p>
                                <p className="font-medium">{vehiculo.modelo || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Año</p>
                                <p className="font-medium">{vehiculo.anio || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Color</p>
                                <p className="font-medium">{vehiculo.color || '-'}</p>
                            </div>
                        </div>
                        {vehiculo.vin && (
                            <div>
                                <p className="text-sm text-muted-foreground">VIN / Chasis</p>
                                <p className="font-mono text-sm">{vehiculo.vin}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Cliente */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Building2 className="h-5 w-5" />
                            Cliente
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {vehiculo.cliente ? (
                            <>
                                <div>
                                    <p className="text-sm text-muted-foreground">Razón Social</p>
                                    <p className="font-medium">{vehiculo.cliente.razon_social || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">RUT</p>
                                    <p className="font-mono">{vehiculo.cliente.rut || '-'}</p>
                                </div>
                                {vehiculo.cliente.email && (
                                    <div>
                                        <p className="text-sm text-muted-foreground">Email</p>
                                        <p className="text-sm">{vehiculo.cliente.email}</p>
                                    </div>
                                )}
                                {vehiculo.cliente.telefono && (
                                    <div>
                                        <p className="text-sm text-muted-foreground">Teléfono</p>
                                        <p className="text-sm">{vehiculo.cliente.telefono}</p>
                                    </div>
                                )}
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="mt-2"
                                    onClick={() => navigate(`/ordenes/clientes/${vehiculo.cliente_id}`)}
                                >
                                    Ver Cliente
                                </Button>
                            </>
                        ) : vehiculo.cliente_razon_social ? (
                            <>
                                <div>
                                    <p className="text-sm text-muted-foreground">Razón Social</p>
                                    <p className="font-medium">{vehiculo.cliente_razon_social}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">RUT</p>
                                    <p className="font-mono">{vehiculo.cliente_rut || '-'}</p>
                                </div>
                                <Badge variant="outline" className="mt-2">Importado de 4sat</Badge>
                            </>
                        ) : (
                            <p className="text-muted-foreground">Sin cliente asignado</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Dispositivos Instalados */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Cpu className="h-5 w-5" />
                        Dispositivos Instalados
                        {vehiculo.dispositivos?.length > 0 && (
                            <Badge variant="outline">{vehiculo.dispositivos.length}</Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {vehiculo.dispositivos?.length > 0 ? (
                        <div className="space-y-3">
                            {vehiculo.dispositivos.map((disp, idx) => (
                                <div key={disp.id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Cpu className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="font-medium">
                                                {disp.tipo_dispositivo || 'GPS'} 
                                                {disp.marca && ` - ${disp.marca}`}
                                                {disp.modelo && ` ${disp.modelo}`}
                                            </p>
                                            <p className="text-sm text-muted-foreground font-mono">
                                                IMEI: {disp.imei || disp.serial || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    {disp.fecha_instalacion && (
                                        <div className="text-right">
                                            <p className="text-xs text-muted-foreground">Instalado</p>
                                            <p className="text-sm">{disp.fecha_instalacion?.split('T')[0] || '-'}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            <Cpu className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Sin dispositivos instalados</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Historial de Servicios */}
            {vehiculo.historial_servicios?.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Historial de Servicios
                            <Badge variant="outline">{vehiculo.historial_servicios.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {vehiculo.historial_servicios.slice(0, 5).map((orden) => (
                                <div 
                                    key={orden.id} 
                                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                                    onClick={() => navigate(`/ordenes/ordenes/${orden.id}`)}
                                >
                                    <div>
                                        <p className="font-medium">{orden.numero_orden}</p>
                                        <p className="text-sm text-muted-foreground">{orden.tipo_servicio}</p>
                                    </div>
                                    <div className="text-right">
                                        <Badge variant={orden.estado === 'facturada' ? 'default' : 'outline'}>
                                            {orden.estado}
                                        </Badge>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {orden.created_at?.split('T')[0]}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Datos de 4sat (si aplica) */}
            {vehiculo.foursat_id && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Hash className="h-5 w-5" />
                            Datos de Sincronización (4sat)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">ID 4sat</p>
                                <p className="font-mono text-sm">{vehiculo.foursat_id}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">IMEI</p>
                                <p className="font-mono text-sm">{vehiculo.foursat_imei || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Grupo</p>
                                <p className="text-sm">{vehiculo.foursat_group || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">GPS</p>
                                <p className="text-sm">{vehiculo.gps_marca} {vehiculo.gps_modelo}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Campos Extra */}
            {vehiculo.campos_extra && Object.keys(vehiculo.campos_extra).length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Campos Personalizados</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {Object.entries(vehiculo.campos_extra).map(([key, value]) => (
                                <div key={key}>
                                    <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                                    <p className="font-medium">{String(value) || '-'}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
